/**
 * PersonaMergePanel - Entity-specific merge panel for Personas
 * 
 * Provides a familiar tab-based UI (Overview | Features) for both
 * source items (view mode) and merged result (edit mode).
 * 
 * This replaces the generic dynamic merge UI when entityType === 'persona',
 * giving users a clear understanding of what data is being kept vs lost.
 * 
 * Props:
 * - sourceItems: array of persona objects fetched from DB
 * - selectedItems: { [idx]: boolean } - which source items are included
 * - expandedItems: { [idx]: boolean } - which source items are expanded
 * - onToggleSelect: (idx) => void
 * - onToggleExpand: (idx) => void
 * - formData: merged persona form data
 * - onFormChange: (field, value) => void
 * - productId: string
 * - errors: { [field]: string } - validation errors
 */

import React, { useState, useEffect } from 'react';
import { cn } from '../../lib/utils';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { useSafeMode } from '../../contexts/SafeModeContext';
import { featuresApi } from '../../services/api';
import {
  ChevronDown,
  ChevronRight,
  Users,
  Package,
  Plus,
  Minus,
  AlertTriangle,
} from 'lucide-react';

// Import reusable form components
import PersonaOverviewForm, { PERSONA_REQUIRED_FIELDS } from '../persona/PersonaOverviewForm';

// =============================================================================
// SOURCE PERSONA CARD (Left Panel)
// =============================================================================

const SourcePersonaCard = ({
  persona,
  index,
  isSelected,
  isExpanded,
  onToggleSelect,
  onToggleExpand,
  linkedFeatureCount,
  colors,
}) => {
  const [activeTab, setActiveTab] = useState('overview');

  return (
    <div 
      className={cn(
        "rounded-lg border overflow-hidden transition-all",
        isSelected 
          ? "border-purple-300 dark:border-purple-600 bg-white dark:bg-gray-800" 
          : "border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 opacity-60"
      )}
      data-testid={`source-persona-${index}`}
    >
      {/* Header - Always Visible */}
      <div 
        className={cn(
          "flex items-center gap-3 p-3 cursor-pointer",
          "hover:bg-gray-50 dark:hover:bg-gray-700/50"
        )}
        onClick={onToggleExpand}
      >
        {/* Selection Checkbox */}
        <div onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={isSelected}
            onCheckedChange={onToggleSelect}
            className="border-gray-400"
          />
        </div>
        
        {/* Expand/Collapse Icon */}
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />
        )}
        
        {/* Persona Icon */}
        <div className={cn(
          "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0",
          isSelected ? "bg-purple-100 dark:bg-purple-900/30" : "bg-gray-200 dark:bg-gray-700"
        )}>
          <Users className={cn("w-4 h-4", isSelected ? "text-purple-600" : "text-gray-500")} />
        </div>
        
        {/* Persona Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={cn(
              "font-medium truncate",
              isSelected ? "text-gray-900 dark:text-gray-100" : "text-gray-500 line-through"
            )}>
              {persona.name || 'Unnamed Persona'}
            </span>
            {!isSelected && (
              <Badge variant="outline" className="text-xs text-red-500 border-red-300">
                Excluded
              </Badge>
            )}
          </div>
          {!isExpanded && (
            <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500">
              <span className="truncate">{persona.role || 'No role'}</span>
              {linkedFeatureCount > 0 && (
                <>
                  <span>•</span>
                  <span>{linkedFeatureCount} feature{linkedFeatureCount !== 1 ? 's' : ''}</span>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Expanded Content - Tab-based View */}
      {isExpanded && (
        <div className={cn("border-t", colors.borderPrimary)}>
          {/* Tabs */}
          <div className="px-3 pt-2">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="bg-gray-100 dark:bg-gray-800">
                <TabsTrigger value="overview" className="text-xs">
                  Overview
                </TabsTrigger>
                <TabsTrigger value="features" className="text-xs">
                  Features ({linkedFeatureCount})
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          
          {/* Tab Content */}
          <div className="p-3">
            {activeTab === 'overview' && (
              <PersonaOverviewForm
                formData={persona}
                isEditing={false}
                isDisabled={true}
                colors={colors}
                mode="view"
              />
            )}
            
            {activeTab === 'features' && (
              <div className={cn("p-4 rounded-lg border text-center", colors.bgTertiary, colors.borderPrimary)}>
                <Package className={cn("w-8 h-8 mx-auto mb-2 opacity-50", colors.textMuted)} />
                <p className={cn("text-sm", colors.textMuted)}>
                  {linkedFeatureCount > 0 
                    ? `${linkedFeatureCount} feature${linkedFeatureCount !== 1 ? 's' : ''} linked to this persona`
                    : 'No features linked to this persona'}
                </p>
                <p className={cn("text-xs mt-1", colors.textMuted)}>
                  Feature links will be transferred to the merged persona
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// MERGE DIFF SUMMARY
// =============================================================================

const MergeDiffSummary = ({ sourceItems, selectedItems, featureCounts, colors }) => {
  // Calculate what's being kept vs lost
  const selectedPersonas = sourceItems.filter((_, idx) => selectedItems[idx]);
  const excludedPersonas = sourceItems.filter((_, idx) => !selectedItems[idx]);
  
  // Count features
  const selectedFeatures = selectedPersonas.reduce((sum, p) => sum + (featureCounts[p.persona_id] || 0), 0);
  const excludedFeatures = excludedPersonas.reduce((sum, p) => sum + (featureCounts[p.persona_id] || 0), 0);

  if (excludedPersonas.length === 0) {
    return null; // No diff to show
  }

  return (
    <div className={cn("p-3 rounded-lg border mb-4", "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700")}>
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle className="w-4 h-4 text-amber-600" />
        <span className="font-medium text-amber-800 dark:text-amber-200 text-sm">
          Merge Impact Summary
        </span>
      </div>
      <div className="space-y-1 text-xs">
        {excludedPersonas.length > 0 && (
          <div className="flex items-center gap-2">
            <Minus className="w-3 h-3 text-red-500" />
            <span className="text-red-700 dark:text-red-300">
              {excludedPersonas.length} persona(s) excluded: {excludedPersonas.map(p => `"${p.name}"`).join(', ')}
            </span>
          </div>
        )}
        {excludedFeatures > 0 && (
          <div className="flex items-center gap-2">
            <Minus className="w-3 h-3 text-red-500" />
            <span className="text-red-700 dark:text-red-300">
              {excludedFeatures} feature link(s) from excluded personas will be lost
            </span>
          </div>
        )}
        <div className="flex items-center gap-2 pt-1 border-t border-amber-300 dark:border-amber-600 mt-2">
          <Plus className="w-3 h-3 text-green-500" />
          <span className="text-green-700 dark:text-green-300">
            Merged persona will have {selectedFeatures} feature link(s) from {selectedPersonas.length} selected persona(s)
          </span>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// MERGED PERSONA FORM (Right Panel)
// =============================================================================

const MergedPersonaForm = ({
  formData,
  onFormChange,
  totalFeatureCount,
  colors,
  errors = {},
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  
  const handleChange = (field, value) => {
    onFormChange(field, value);
  };

  // Check if there are errors in overview fields
  const hasOverviewErrors = Object.keys(errors).some(k => PERSONA_REQUIRED_FIELDS.includes(k));

  return (
    <div className="h-full flex flex-col">
      {/* Tabs */}
      <div className="flex-shrink-0 pb-2">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className={cn("bg-gray-100 dark:bg-gray-800")}>
            <TabsTrigger 
              value="overview" 
              className={cn("text-sm", hasOverviewErrors && "text-red-500")}
            >
              Overview {hasOverviewErrors && '⚠️'}
            </TabsTrigger>
            <TabsTrigger value="features" className="text-sm">
              Features ({totalFeatureCount})
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      
      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'overview' && (
          <PersonaOverviewForm
            formData={formData}
            onChange={handleChange}
            isEditing={true}
            isDisabled={false}
            colors={colors}
            errors={errors}
            mode="create"
          />
        )}
        
        {activeTab === 'features' && (
          <div className={cn("p-4 rounded-lg border text-center", colors.bgTertiary, colors.borderPrimary)}>
            <Package className={cn("w-8 h-8 mx-auto mb-2 opacity-50", colors.textMuted)} />
            <p className={cn("text-sm font-medium", colors.textPrimary)}>
              {totalFeatureCount} feature{totalFeatureCount !== 1 ? 's' : ''} will be linked
            </p>
            <p className={cn("text-xs mt-2", colors.textMuted)}>
              All features from selected personas will be linked to the merged persona.
              You can manage feature links after the merge is complete.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

const PersonaMergePanel = ({
  sourceItems,
  selectedItems,
  expandedItems,
  onToggleSelect,
  onToggleExpand,
  formData,
  onFormChange,
  productId,
  errors = {},
}) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  
  // Feature counts per persona
  const [featureCounts, setFeatureCounts] = useState({});
  const [loadingFeatures, setLoadingFeatures] = useState(true);
  
  // Load feature counts for each persona
  useEffect(() => {
    const loadFeatureCounts = async () => {
      if (!productId || sourceItems.length === 0) {
        setLoadingFeatures(false);
        return;
      }
      
      try {
        const res = await featuresApi.listByProduct(productId);
        const allFeatures = res.data || [];
        
        // Count features per persona
        const counts = {};
        sourceItems.forEach(persona => {
          counts[persona.persona_id] = allFeatures.filter(
            f => f.persona_ids?.includes(persona.persona_id)
          ).length;
        });
        
        setFeatureCounts(counts);
      } catch (err) {
        console.error('Failed to load features:', err);
      } finally {
        setLoadingFeatures(false);
      }
    };
    
    loadFeatureCounts();
  }, [productId, sourceItems]);
  
  // Calculate total feature count for selected personas
  const totalFeatureCount = sourceItems
    .filter((_, idx) => selectedItems[idx])
    .reduce((sum, p) => sum + (featureCounts[p.persona_id] || 0), 0);
  
  // Selected count
  const selectedCount = Object.values(selectedItems).filter(Boolean).length;

  return (
    <div className="grid grid-cols-2 h-full divide-x divide-gray-200 dark:divide-gray-700">
      {/* Left Panel: Source Personas */}
      <div className="flex flex-col overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-gray-900 dark:text-gray-100">
              Source Personas
            </h3>
            <Badge variant="outline">
              {selectedCount} of {sourceItems.length} selected
            </Badge>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Select personas to include in the merge. Expand to review data.
          </p>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {sourceItems.map((persona, idx) => (
            <SourcePersonaCard
              key={persona.persona_id || idx}
              persona={persona}
              index={idx}
              isSelected={selectedItems[idx]}
              isExpanded={expandedItems[idx]}
              onToggleSelect={() => onToggleSelect(idx)}
              onToggleExpand={() => onToggleExpand(idx)}
              linkedFeatureCount={featureCounts[persona.persona_id] || 0}
              colors={colors}
            />
          ))}
        </div>
      </div>
      
      {/* Right Panel: Merged Persona Form */}
      <div className="flex flex-col overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
          <h3 className="font-medium text-gray-900 dark:text-gray-100">
            Merged Result
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Edit the combined persona before applying
          </p>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4">
          {/* Diff Summary */}
          <MergeDiffSummary
            sourceItems={sourceItems}
            selectedItems={selectedItems}
            featureCounts={featureCounts}
            colors={colors}
          />
          
          {/* Merged Form */}
          <MergedPersonaForm
            formData={formData}
            onFormChange={onFormChange}
            totalFeatureCount={totalFeatureCount}
            colors={colors}
            errors={errors}
          />
        </div>
      </div>
    </div>
  );
};

export default PersonaMergePanel;
